import { updateProductApiV1ProductsOrgIdProductsProductIdPut } from '@/common/api/client/sdk.gen'
import { client } from '@/common/lib/fetch'
import type { paths } from '@/common/types/api/schema'
import type {
    GetProductResponse,
    GenerateTitleDetailRequest,
    GenerateTitleDetailResponse,
    CreateProductRequest,
    CreateProductResponse,
    ValidateProductResponse,
    GenerateTitleResponse,
} from '@/common/types/api'
export * from './media'
export type {
    GetProductResponse,
    GenerateTitleDetailRequest,
    GenerateTitleDetailResponse,
    CreateProductRequest,
    CreateProductResponse,
    ValidateProductResponse,
    GenerateTitleResponse,
    //GetProductAssetsResponse,
}

export type ValidateProductRequest = {
    org_id: string
    product_id: string
}

export type GenerateTitleRequestPayload = {
    org_id: string
    product_id: string
    description_tone?: 'RESTRAINED' | 'WARM' | 'TECHNICALLY' | 'CHEERFULLY' | 'SELLING'
    key_words?: string
}

export const getProduct = async (orgId: string, productId: string): Promise<GetProductResponse> => {
    const response = await client.get<GetProductResponse>(`/api/v1/products/${orgId}/products/${productId}`)
    return response.data
}

type CreateProductPayload = Omit<CreateProductRequest, 'image_files'> & {
    image_files: File[]
    brand?: string | null
}

export const createProduct = async (org_id: string, data: CreateProductPayload): Promise<CreateProductResponse> => {
    const formData = new FormData()

    formData.append('title', data.title)
    formData.append('description', data.description)
    formData.append('marketplace', data.marketplace)

    if (data.brand) {
        formData.append('brand', data.brand)
    }

    data.image_files.forEach(file => {
        formData.append('image_files', file)
    })

    const response = await client.post<CreateProductResponse>(`/api/v1/products/${org_id}/create`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    })

    if (data.brand && response.data?.id) {
        await updateProductApiV1ProductsOrgIdProductsProductIdPut({
            path: { org_id, product_id: response.data.id },
            body: { brand_id: data.brand },
        })
    }

    return response.data
}

export const validateProductSku = async (
    data: ValidateProductRequest,
    is_ro_re: boolean = false
): Promise<ValidateProductResponse> => {
    const { org_id, product_id } = data
    const response = await client.post<ValidateProductResponse>(
        `/api/v1/generations/${org_id}/validate-product/product/${product_id}`,
        {
            is_ro_re,
        },
        {
            headers: { 'Content-Type': 'application/json' },
        }
    )
    return response.data
}

export const generateTitle = async (data: GenerateTitleRequestPayload): Promise<GenerateTitleResponse> => {
    const { org_id, product_id, description_tone, key_words } = data
    const response = await client.post<GenerateTitleResponse>(
        `/api/v1/generations/${org_id}/generate-title/product/${product_id}`,
        {
            description_tone,
            key_words,
        }
    )
    return response.data
}

export const getGenerateTitleDetail = async (
    data: GenerateTitleDetailRequest
): Promise<GenerateTitleDetailResponse> => {
    const { org_id, generation_id } = data
    const response = await client.get<GenerateTitleDetailResponse>(
        `/api/v1/generations/${org_id}/generation/${generation_id}/generate-title`
    )
    return response.data
}

export interface GetCeleryTaskResponse {
    task_id: string
    state: string
    result?: {
        result?: {
            generation_id?: string
        }
    }
    traceback?: string
}

export const getCeleryTask = async (taskId: string): Promise<GetCeleryTaskResponse> => {
    const response = await client.get<GetCeleryTaskResponse>(`/api/v1/celery/task/${taskId}`)
    return response.data
}

export interface GenerationStatusResponse {
    status: string
    id: string
    [key: string]: unknown
}

export const getGenerationStatus = async (orgId: string, generationId: string): Promise<GenerationStatusResponse> => {
    const response = await client.get<GenerationStatusResponse>(
        `/api/v1/generations/${orgId}/generation/${generationId}/card-validation`
    )
    return response.data
}

export interface ProductStatusResponse {
    status: string
    id: string
    [key: string]: unknown
}

export const getProductStatus = async (orgId: string, productId: string): Promise<ProductStatusResponse> => {
    const response = await client.get<ProductStatusResponse>(`/api/v1/products/${orgId}/products/${productId}`)
    return response.data
}

export type GetProductAssetsResponse =
    paths['/api/v1/assets/{org_id}/products/{product_id}']['get']['responses'][200]['content']['application/json']

export const getProductAssets = async (
    orgId: string,
    productId: string,
    productAssetType?: 'media' | 'uploaded' | 'generated' | null
): Promise<GetProductAssetsResponse> => {
    const params = productAssetType ? { product_asset_type: productAssetType } : undefined
    const response = await client.get<GetProductAssetsResponse>(`/api/v1/assets/${orgId}/products/${productId}`, {
        params,
    })
    return response.data
}
